import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  SxProps,
  Typography,
} from "@mui/material"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { NodeResult, RunDto, RunNodeTimestepResponseDto, RunResults } from "@custom-types/modelRun"
import {
  ProbabilityDensityChart,
  ProbabilityDensityChartData,
} from "@features/results/Result/ProbabilityDensityChart"
import { KernelEstimationFunction, calcDistribution } from "@features/results/Result/const"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import * as d3 from "d3"
import { InterquartileRange } from "@features/results/Result/InterquartileRange"
import { Mark } from "@mui/material/Slider/useSlider.types"
import "./ProbabilityDensityContainer.css"
import Button from "@mui/material/Button"
import DownloadChartImageDialog from "@features/results/DownloadChartImageDialog"
import { useLocalStorage } from "usehooks-ts"
import { areResultsOfTheSameUnitFormat, getRunNamesMap } from "@utils/run"
import { GroupNodeLabel } from "@features/graph/graphViewModel"

const DEFAULT_CHART_OPTIONS: ProbabilityDensityChartOptions = {
  isHistogramShown: true,
  isEstimationShown: false,
  isIQRangeVisible: true,
  isViolinDiagram: false,
  kernel: [KernelEstimationFunction.Epanechnikov],
  year: 0,
  secondYear: 0,
  sliderThresholdValue: 60,
  sliderEstimationBandwidthValue: 1,
  isSecondYear: false,
  isDemean: false,
  minVal: 0,
  maxVal: 0,
  clamp: false,
  clamp_stdev: 1,
  clamp_string: "Show area % within " + 1 + " st.dev",
  isCDF: false,
  showSuprenums: false,
}

export type ProbabilityDensityChartResult = Pick<RunNodeTimestepResponseDto, "items">

type ProbabilityDensityContainerProps = {
  rawResultDataFilterNodes?: RunResults
  isFetchingGetRunFilterNode: boolean
  lagShift: number
  selectedNode?: GroupNodeLabel | null
  runs: RunDto[]
  localStoragePrefix: string
  showYearSlider?: boolean
  chartTitle?: string
  getDatasetLabel?: (timestepData: NodeResult) => string
  onlyChart?: boolean
  chartOptions?: ProbabilityDensityChartOptions
  maintainAspectRatio?: boolean
  chartContainerSx?: SxProps
  sx?: SxProps
  legendPosition?: "top" | "bottom"
  showLegend?: boolean
  hiddenDatasets?: string[]
  onLegendClick?: (datasetLabel: string) => void
}

export type ProbabilityDensityChartOptions = {
  isHistogramShown: boolean
  isEstimationShown: boolean
  isIQRangeVisible: boolean
  isViolinDiagram: boolean
  kernel: KernelEstimationFunction[]
  year: number
  secondYear: number
  sliderThresholdValue: number
  sliderEstimationBandwidthValue: number
  isSecondYear: boolean
  isDemean: boolean
  minVal: number
  maxVal: number
  clamp: boolean
  clamp_stdev: number
  clamp_string: string
  isCDF: boolean
  showSuprenums: boolean
}

export function ProbabilityDensityContainer({
  rawResultDataFilterNodes,
  isFetchingGetRunFilterNode,
  lagShift = 0,
  selectedNode,
  runs,
  localStoragePrefix,
  showYearSlider = true,
  chartTitle,
  getDatasetLabel,
  onlyChart = false,
  chartContainerSx = { height: 500 },
  legendPosition = "top",
  showLegend = true,
  sx,
  hiddenDatasets = [],
  onLegendClick,
  ...props
}: ProbabilityDensityContainerProps) {
  const [data, setData] = useState<ProbabilityDensityChartData[]>([])
  const [secondYearData, setSecondYearData] = useState<[string[], number[]]>([[], []])
  const [iqrData, setIqrData] = useState<number[][]>([[]])
  const [iqrSecondYearData, setIqrSecondYearData] = useState<number[]>([])
  const [adjustedData, setAdjustedData] = useState<number[][]>([[]])
  const [adjustedSecondYearData, setAdjustedSecondYearData] = useState<number[]>([])

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [localChartOptions, setLocalChartOptions] = useState<ProbabilityDensityChartOptions>(
    props.chartOptions ?? DEFAULT_CHART_OPTIONS,
  )
  const [globalChartOptions, setGlobalChartOptions] =
    useLocalStorage<ProbabilityDensityChartOptions>(
      `${localStoragePrefix}_probabilityDensityChart`,
      DEFAULT_CHART_OPTIONS,
    )

  // if chart options are passed, treat them as "starting options", otherwise use "global" options from local storage
  const options = props.chartOptions ? localChartOptions : globalChartOptions
  const setOptions = props.chartOptions ? setLocalChartOptions : setGlobalChartOptions

  const nodeResults: NodeResult[][] = rawResultDataFilterNodes
    ? Object.values(rawResultDataFilterNodes).reduce(
        (res, curr) => [...res, ...Object.values(curr)],
        [] as NodeResult[][],
      )
    : []

  const years: Mark[] = useMemo(() => {
    if (!rawResultDataFilterNodes) return []

    const unionTimesteps: Set<number> = new Set()
    nodeResults.forEach((results) => results.map((result) => unionTimesteps.add(result.timestep)))
    const yearsAll = Array.from(unionTimesteps).sort()

    const years = yearsAll.flatMap((x: number, index) => {
      if (index >= lagShift) return [{ value: index - lagShift, label: x }]
      else return []
    })

    return years
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [rawResultDataFilterNodes])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  const chartRef = useRef<any>(null)
  const chartContainerRef = useRef<HTMLDivElement | null>(null)

  const runNamesMap = getRunNamesMap(runs)
  const runIds = runs.map((run) => run.key)

  const calcYears = (min: number, max: number) => {
    if (
      Object.values(rawResultDataFilterNodes ?? {}).length === 0 ||
      !options.sliderThresholdValue
    ) {
      return
    }

    const items = nodeResults.map((results) =>
      results.find((result) => result.timestep === years[options.year]?.label),
    )

    const item2 = options.isSecondYear
      ? // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        nodeResults[0].find((x) => x.timestep === years[options.secondYear]?.label)
      : undefined // needs to be every other run in rawResultDataFilterNodes

    const meanOffsets1 = items.map((item) => {
      return d3.mean(item?.data || []) || 0
    })
    const meanOffset2 = d3.mean(item2?.data || []) || 0

    const result: {
      xValues: string[]
      yValues: number[]
      customLabel?: string
      nodeName?: string
      nodeUnit?: string
      runName?: string
      runVersion?: string
    }[] = []
    const rawResult: number[][] = []
    const iqrResult: number[][] = []

    nodeResults.forEach((nodeData, i) => {
      const item = nodeData.find((x) => x.timestep === years[options.year]?.label)

      if (item) {
        const adjustedItems = item.data.map(
          (num) =>
            num -
            (options.isDemean
              ? // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                meanOffsets1[i] + (i > 0 ? meanOffsets1[i] - meanOffsets1[0] : 0)
              : 0),
        )
        const calc = calcDistribution(
          adjustedItems,
          options.sliderThresholdValue,
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          min - (options.isDemean ? meanOffsets1[i] : 0),
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          max - (options.isDemean ? meanOffsets1[i] : 0),
        )

        const currentRun = runs.find((run) => run.key === item.key)
        result.push({
          xValues: calc[0],
          yValues: calc[1],
          customLabel: getDatasetLabel?.(item) ?? "",
          nodeName: selectedNode?.label,
          nodeUnit: selectedNode?.format,
          runName: currentRun ? runNamesMap[currentRun.key] : undefined,
          runVersion: currentRun?.version,
        })
        iqrResult.push(item.data)
        rawResult.push(adjustedItems)
      }
    })

    setData(result)
    setAdjustedData(rawResult)
    setIqrData(iqrResult)

    if (item2) {
      const adjustedItem2 = item2.data.map((num) => num - (options.isDemean ? meanOffset2 : 0))
      const result2 = calcDistribution(
        adjustedItem2,
        options.sliderThresholdValue,
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        min - (options.isDemean ? meanOffsets1[0] : 0),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        max - (options.isDemean ? meanOffsets1[0] : 0),
      )
      setSecondYearData(result2)
      setAdjustedSecondYearData(adjustedItem2)
      setIqrSecondYearData(item2.data.map((num) => num))
    }
  }

  useEffect(() => {
    if (Object.values(rawResultDataFilterNodes ?? {}).length === 0) {
      return
    }

    let minValCalc = 0
    let maxValCalc = 0
    // we may have different periods for runs, so we need to work out min and max from all of them

    const minValArr: number[] = []
    const maxValArr: number[] = []

    nodeResults.forEach((nodeData) => {
      for (let i = lagShift; i < nodeData.length; i++) {
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        minValArr.push(d3.min(nodeData[i].data || []) || 0)
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        maxValArr.push(d3.max(nodeData[i].data || []) || 0)
      }
    })

    minValCalc = d3.min(minValArr) || 0
    maxValCalc = d3.max(maxValArr) || 0

    setOptions((prevState) => {
      return {
        ...prevState,
        minVal: minValCalc,
        maxVal: maxValCalc,
        year: years.find((x) => x.value === options.year) ? options.year : years[0]?.value || 0,
        secondYear: years.find((x) => x.value === options.year)
          ? options.secondYear
          : years[0]?.value || 0,
      }
    })

    calcYears(minValCalc, maxValCalc)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [rawResultDataFilterNodes])

  useEffect(() => {
    if (isNaN(options.minVal) || isNaN(options.maxVal)) return

    calcYears(options.minVal, options.maxVal)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [
    options.year,
    options.sliderThresholdValue,
    options.isSecondYear,
    options.secondYear,
    options.isDemean,
    options.minVal,
    options.maxVal,
  ])

  const areTheSameUnitsUsed = areResultsOfTheSameUnitFormat(rawResultDataFilterNodes)

  if (!areTheSameUnitsUsed) {
    return (
      <Typography>
        Can not render <strong>probability density chart</strong> because selected node(s) are using
        different units.
      </Typography>
    )
  }

  const handleKernelChange = (event: SelectChangeEvent) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
    setOptions((prevState: any) => {
      return {
        ...prevState,
        kernel: event.target.value,
      }
    })
  }

  const handleCDFChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        isCDF: event.target.checked,
        clamp: event.target.checked && options.clamp ? false : options.clamp,
        isDemean: event.target.checked ? true : options.isDemean,
      }
    })
  }

  const handleSuprenumChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        showSuprenums: event.target.checked,
        isCDF: !options.isCDF && event.target.checked ? true : options.isCDF,
        isEstimationShown:
          !options.isEstimationShown && event.target.checked ? true : options.isEstimationShown,
        isHistogramShown: event.target.checked ? false : options.isHistogramShown,
      }
    })
  }

  const handleStdevChange = (event: SelectChangeEvent) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        clamp_stdev: +event.target.value,
        clamp_string: "Show area % within " + event.target.value + " st.dev",
      }
    })
  }

  const onSliderThresholdChange = (event: Event, value: number | number[]) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        sliderThresholdValue: +value,
      }
    })
  }

  const onSliderEstimationBandwidthChange = (event: Event, value: number | number[]) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        sliderEstimationBandwidthValue: +value,
      }
    })
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleChangeHistogramShown = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        isHistogramShown: event.target.checked,
      }
    })
  }

  const handleChangeEstimation = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        isEstimationShown: event.target.checked,
      }
    })
  }

  const handleChangeIQRange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        isIQRangeVisible: event.target.checked,
      }
    })
  }

  const handleChangeViolinDiagram = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        isViolinDiagram: event.target.checked,
      }
    })
  }

  const handleClamp = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        clamp: event.target.checked,
      }
    })
  }

  const onSliderChange = (event: Event, newValue: number | number[]) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        year: newValue as number,
      }
    })
  }

  const onSliderSecondYearChange = (event: Event, newValue: number | number[]) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        secondYear: newValue as number,
      }
    })
  }

  const handleChangeCheckboxSecondYear = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        isSecondYear: event.target.checked,
        isDemean: prevState.isDemean && event.target.checked,
      }
    })
  }

  const handleChangeCheckboxDemean = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        isDemean: event.target.checked,
      }
    })
  }

  const handleResetZoom = () => {
    if (chartRef && chartRef.current) {
      // in this case on zoom we're recalculating data sets, so instead of reseting zoom
      // we want to bring back original data sets
      calcYears(options.minVal, options.maxVal)
    }
  }

  return (
    <Grid container spacing={1} sx={sx}>
      {!onlyChart && (
        <Grid item xs={12}>
          <Box>
            <Box
              onClick={handleClick}
              sx={{ height: "48px", lineHeight: "48px", display: "inline-block" }}
            >
              {anchorEl ? (
                <KeyboardArrowUpIcon
                  sx={{
                    verticalAlign: "middle",
                    position: "relative",
                    bottom: "2px",
                  }}
                />
              ) : (
                <KeyboardArrowDownIcon
                  sx={{
                    verticalAlign: "middle",
                    position: "relative",
                    bottom: "2px",
                  }}
                />
              )}
              {"Chart options"}
            </Box>
            <Button onClick={handleResetZoom} variant={"contained"} sx={{ ml: 2 }}>
              Reset zoom
            </Button>
            <Menu
              id="menu"
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={handleClose}
              MenuListProps={{
                "aria-labelledby": "menu",
                sx: { width: "300px" },
              }}
            >
              <FormGroup sx={{ ml: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={options.isHistogramShown}
                      onChange={handleChangeHistogramShown}
                    />
                  }
                  label="Show histogram"
                />
              </FormGroup>
              <Typography sx={{ ml: 1 }} id="input-slider" gutterBottom>
                Number of Bins
              </Typography>
              <Slider
                sx={{ ml: 2, width: "220px" }}
                aria-labelledby="input-slider"
                min={10}
                max={200}
                defaultValue={60}
                value={options.sliderThresholdValue}
                onChange={onSliderThresholdChange}
                valueLabelDisplay="auto"
              />
              <FormGroup sx={{ ml: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={options.isDemean}
                      onChange={handleChangeCheckboxDemean}
                      disabled={!years.length || options.isCDF}
                    />
                  }
                  label="De-Mean (centre zero) data"
                />
                <Divider></Divider>
                <FormControlLabel
                  control={<Checkbox checked={options.isCDF} onChange={handleCDFChange} />}
                  label="Show Cumulative Density"
                />
                {data.length == 1 ? null : (
                  <FormControlLabel
                    control={
                      <Checkbox checked={options.showSuprenums} onChange={handleSuprenumChange} />
                    }
                    label="Show CDF Suprenums"
                  />
                )}
              </FormGroup>
              <Divider></Divider>
              <FormGroup sx={{ ml: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={options.isEstimationShown}
                      onChange={handleChangeEstimation}
                    />
                  }
                  label="Show PDE estimations"
                />
                <Typography sx={{ ml: 1 }} id="input-slider" gutterBottom>
                  PDE Smoothness Bandwidth
                </Typography>
                <Slider
                  sx={{ ml: 2, width: "220px" }}
                  aria-labelledby="input-slider"
                  min={0.5}
                  max={20.0}
                  shiftStep={1}
                  step={0.1}
                  track="inverted"
                  defaultValue={1.0}
                  value={options.sliderEstimationBandwidthValue}
                  onChange={onSliderEstimationBandwidthChange}
                  valueLabelDisplay="on"
                />
                <Typography sx={{ ml: 1 }} id="input-slider" gutterBottom>
                  Kernel type
                </Typography>
                <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                  <Select
                    multiple={true}
                    // @ts-ignore
                    value={options.kernel}
                    onChange={handleKernelChange}
                    label="Kernel type"
                  >
                    <MenuItem value={KernelEstimationFunction.Epanechnikov}>Epanechnikov</MenuItem>
                    <MenuItem value={KernelEstimationFunction.Gaussian}>Gaussian</MenuItem>
                    <MenuItem value={KernelEstimationFunction.Uniform}>Uniform</MenuItem>
                    <MenuItem value={KernelEstimationFunction.Triangular}>Triangular</MenuItem>
                  </Select>
                </FormControl>
                <Divider></Divider>
              </FormGroup>
              {options.isCDF || !options.isEstimationShown || data.length > 1 ? null : (
                <FormGroup sx={{ ml: 1 }}>
                  <FormControlLabel
                    control={<Checkbox checked={options.clamp} onChange={handleClamp} />}
                    label={options.clamp_string}
                  />
                  <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                    <Select
                      // @ts-ignore
                      value={options.clamp_stdev}
                      onChange={handleStdevChange}
                      label="Standard deviations"
                    >
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={2}>2</MenuItem>
                      <MenuItem value={3}>3</MenuItem>
                    </Select>
                  </FormControl>
                  <Divider></Divider>
                </FormGroup>
              )}

              <FormGroup sx={{ ml: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox checked={options.isIQRangeVisible} onChange={handleChangeIQRange} />
                  }
                  label="Display IQR"
                />
              </FormGroup>
              <FormGroup sx={{ ml: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={options.isViolinDiagram}
                      onChange={handleChangeViolinDiagram}
                      disabled={!options.isIQRangeVisible}
                    />
                  }
                  label="Show IQR as Violin"
                />
              </FormGroup>
              {rawResultDataFilterNodes &&
                Object.values(rawResultDataFilterNodes ?? {}).length <= 1 && (
                  <FormGroup sx={{ ml: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.isSecondYear}
                          onChange={handleChangeCheckboxSecondYear}
                          disabled={!years.length || isFetchingGetRunFilterNode}
                        />
                      }
                      label="Show Additional Timestep"
                    />
                  </FormGroup>
                )}
            </Menu>
            <DownloadChartImageDialog
              buttonProps={{
                disabled: isFetchingGetRunFilterNode,
                sx: { ml: 1 },
              }}
              chartRef={chartContainerRef}
              defaultFileName="Probability Density Chart"
              runReference={runIds.join(" vs. ")}
            />
          </Box>
        </Grid>
      )}
      <Grid container spacing={1} ref={chartContainerRef} sx={sx}>
        <Grid item={true} xs={12} sx={sx}>
          {isFetchingGetRunFilterNode ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={chartContainerSx}>
              {!onlyChart && (
                <Typography variant="h6" textAlign="center" sx={{ mb: 1 }}>
                  {chartTitle ? chartTitle : `Probability density for ${selectedNode?.label}`}
                </Typography>
              )}
              <ProbabilityDensityChart
                adjusted={adjustedData}
                adjusted2={[adjustedSecondYearData]}
                data={data}
                secondYearData={secondYearData}
                isSecondYearSelected={options.isSecondYear}
                showHistograms={options.isHistogramShown}
                showEstimations={options.isEstimationShown}
                estimationBandwidth={options.sliderEstimationBandwidthValue}
                kernel={options.kernel}
                clamp={options.clamp}
                clampStDev={options.clamp_stdev}
                isCDF={options.isCDF}
                showSuprenums={options.showSuprenums}
                axisFormat={selectedNode?.format}
                ref={chartRef}
                onZoomChange={({ xMax, xMin }) => calcYears(xMin, xMax)}
                maintainAspectRatio={props.maintainAspectRatio}
                legendPosition={legendPosition}
                showLegend={showLegend}
                hiddenDatasets={hiddenDatasets}
                onLegendClick={onLegendClick}
              />
            </Box>
          )}
        </Grid>
        {showYearSlider && (
          <Grid item={true} xs={12}>
            <Box sx={{ ml: 2, mt: 1 }}>
              <Slider
                className={"probability-density-slider"}
                sx={{ mt: 2 }}
                min={0}
                max={years?.length || 0}
                marks={years}
                defaultValue={years[0]?.value || 0}
                value={options.year}
                onChange={onSliderChange}
                disabled={!years.length || isFetchingGetRunFilterNode}
                valueLabelDisplay={years.length ? "auto" : "off"}
                step={null}
              />
            </Box>
          </Grid>
        )}
        {rawResultDataFilterNodes &&
          Object.values(rawResultDataFilterNodes ?? {}).length <= 1 &&
          options.isSecondYear && (
            <>
              <Grid item={true} xs={12}>
                <Box sx={{ ml: 2 }}>
                  <Slider
                    className={"probability-density-slider"}
                    sx={{ mt: 3 }}
                    min={0}
                    max={years?.length || 0}
                    marks={years}
                    defaultValue={years[0]?.value || 0}
                    value={options.secondYear}
                    step={null}
                    onChange={onSliderSecondYearChange}
                    disabled={!years.length || !options.isSecondYear || isFetchingGetRunFilterNode}
                    valueLabelDisplay={years.length ? "auto" : "off"}
                  />
                </Box>
              </Grid>
            </>
          )}
        {!onlyChart && options.isIQRangeVisible && (
          <Grid item={true} xs={12} sx={{ mt: 2 }}>
            {isFetchingGetRunFilterNode ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <InterquartileRange
                data={iqrData || []}
                dataSecondYear={options.isSecondYear ? iqrSecondYearData || [] : []}
                minValue={options.minVal}
                maxValue={options.maxVal}
                isViolinChart={options.isViolinDiagram}
                axisFormat={selectedNode?.format}
              />
            )}
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}
